import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Clock, Users } from "lucide-react";
import { useAttendance } from "../../hooks/useAttendance";
import { useAuth } from "@/hooks/useAuth";
import { CreateShiftRequest, days, WorkShift } from "@/types/attendance";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { TimePicker } from "@/components/ui/time-picker";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

export const ShiftManager = () => {
  const { company } = useAuth();
  const { shifts, createShift, deleteShift, isLoadingShifts } = useAttendance(company?.slug || "");
  console.log("📂 [ShiftManager] Loaded shifts:", shifts);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newShift, setNewShift] = useState<Partial<CreateShiftRequest>>({
    name: "",
    start_time: "08:00",
    end_time: "17:00",
    work_days: [1, 2, 3, 4, 5],
  });

  const handleCreate = async () => {
    if (!newShift.name || !newShift.start_time || !newShift.end_time || !newShift.work_days?.length) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    try {
      await createShift.mutateAsync({
        ...newShift as CreateShiftRequest,
        slug: company?.slug || "",
      });
      setIsModalOpen(false);
      setNewShift({
        name: "",
        start_time: "08:00",
        end_time: "17:00",
        work_days: [1, 2, 3, 4, 5],
      });
      toast.success("Turno creado correctamente");
    } catch (error) {
      toast.error("Error al crear el turno");
    }
  };

  const getDayLabels = (workDays: number[]) => {
    return days
      .filter((d) => workDays.includes(d.id))
      .map((d) => d.label)
      .join(", ");
  };

  if (isLoadingShifts) return <div>Cargando turnos...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Gestión de Turnos</h3>
        <Button onClick={() => setIsModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Nuevo Turno
        </Button>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Horario</TableHead>
              <TableHead>Días Lab.</TableHead>
              <TableHead className="w-[180px]">Personal Asignado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shifts?.map((shift: WorkShift) => (
              <TableRow key={shift.id}>
                <TableCell className="font-medium">{shift.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    {shift.start_time.substring(0, 5)} - {shift.end_time.substring(0, 5)}
                  </div>
                </TableCell>
                <TableCell>{getDayLabels(shift.work_days)}</TableCell>
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 cursor-help group">
                          <Users className="h-4 w-4 text-blue-500 group-hover:text-blue-600 transition-colors" />
                          <Badge variant="secondary" className={`bg-blue-50 text-blue-700 hover:bg-blue-100 ${!shift.employees?.length ? 'opacity-40' : ''}`}>
                            {shift.employees?.length || 0}
                          </Badge>
                          <span className="text-[10px] text-slate-400 font-medium">pers.</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="p-3 max-w-[250px] bg-white border shadow-xl">
                        {shift.employees && shift.employees.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-xs font-bold text-slate-500 uppercase pb-1 border-b">Personal Asignado</p>
                            <ul className="space-y-1.5">
                              {shift.employees.map(emp => (
                                <li key={emp.id} className="text-xs text-slate-700 flex items-center gap-2">
                                  <div className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                                  {emp.name}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 italic">No hay empleados asignados</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteShift.mutate(shift.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Turno</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre del Turno</Label>
              <Input
                id="name"
                placeholder="Ej: Diurno Central"
                value={newShift.name}
                onChange={(e) => setNewShift({ ...newShift, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora Entrada</Label>
                <TimePicker
                  value={newShift.start_time}
                  onChange={(time) => setNewShift({ ...newShift, start_time: time })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Salida</Label>
                <TimePicker
                  value={newShift.end_time}
                  onChange={(time) => setNewShift({ ...newShift, end_time: time })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Días Laborales</Label>
              <div className="grid grid-cols-4 gap-2">
                {days.map((day) => (
                  <div key={day.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`day-${day.id}`}
                      checked={newShift.work_days?.includes(day.id)}
                      onCheckedChange={(checked) => {
                        const current = newShift.work_days || [];
                        const updated = checked
                          ? [...current, day.id]
                          : current.filter((id) => id !== day.id);
                        setNewShift({ ...newShift, work_days: updated });
                      }}
                    />
                    <label
                      htmlFor={`day-${day.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate}>Crear Turno</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
